import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import HelpArticleService from '../../../services/helpArticleService';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Textarea from '../../../components/ui/Textarea';
import FileUpload from '../../../components/ui/FileUpload';
import Icon from '../../../components/AppIcon';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const ArticleEditor = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { articleId } = useParams();
  const [searchParams] = useSearchParams();
  
  // Support both old route params and new query params
  const actualArticleId = articleId || searchParams.get('articleId');
  const isEditMode = !!actualArticleId;

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    content: '',
    category: 'getting-started',
    icon_name: 'HelpCircle',
    urgency: 'medium',
    estimated_time: '',
    helpful_for: ['va', 'employer'],
    tags: [],
    steps: [],
    is_published: false,
    thumbnail_url: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [stepInput, setStepInput] = useState('');
  const [stepDescInput, setStepDescInput] = useState('');
  const [expandedSteps, setExpandedSteps] = useState({});
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [autoGenerateSlug, setAutoGenerateSlug] = useState(!isEditMode);
  const [notification, setNotification] = useState(null);

  // Categories
  const categories = [
    { id: 'getting-started', label: 'Getting Started' },
    { id: 'profile', label: 'Profile & Settings' },
    { id: 'jobs', label: 'Jobs & Applications' },
    { id: 'messaging', label: 'Communication' },
    { id: 'payment', label: 'Payment & Billing' },
    { id: 'technical', label: 'Technical Issues' },
    { id: 'policies', label: 'Policies & Guidelines' }
  ];

  // Available icons
  const availableIcons = [
    'HelpCircle', 'User', 'Play', 'Briefcase', 'MessageSquare', 'CreditCard',
    'Settings', 'Shield', 'Eye', 'Building', 'Target', 'Filter', 'MessageCircle',
    'Users', 'FileText', 'AlertCircle', 'Globe', 'BookOpen', 'Scale', 'Plus',
    'Check', 'Info', 'Star', 'Award', 'TrendingUp', 'Clock', 'Calendar'
  ];

  // Quill editor configuration
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      ['link', 'code-block'],
      [{ align: [] }],
      ['clean']
    ]
  }), []);

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'indent', 'link', 'code-block', 'align'
  ];

  // Check admin access
  useEffect(() => {
    if (userProfile && userProfile.role !== 'admin' && userProfile.role !== 'moderator') {
      navigate('/dashboard');
    }
  }, [userProfile, navigate]);

  // Load article if editing
  useEffect(() => {
    if (isEditMode) {
      loadArticle();
    }
  }, [actualArticleId, isEditMode]);

  const loadArticle = async () => {
    try {
      const result = await HelpArticleService.getArticleById(actualArticleId);
      if (result.success) {
        // Ensure all array fields have proper defaults
        const articleData = {
          ...result.data,
          tags: result.data.tags || [],
          steps: result.data.steps || [],
          helpful_for: result.data.helpful_for || ['va', 'employer']
        };
        setFormData(articleData);
        setAutoGenerateSlug(false);
      } else {
        showNotification('error', 'Failed to load article');
        navigate('/admin-moderation?tab=help-cms');
      }
    } catch (error) {
      showNotification('error', 'An error occurred while loading the article');
      navigate('/admin-moderation?tab=help-cms');
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate slug from title
  useEffect(() => {
    if (autoGenerateSlug && formData.title && !isEditMode) {
      const slug = HelpArticleService.generateSlug(formData.title);
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.title, autoGenerateSlug, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleContentChange = (value) => {
    setFormData(prev => ({ ...prev, content: value }));
    if (errors.content) {
      setErrors(prev => ({ ...prev, content: '' }));
    }
  };

  const handleHelpfulForToggle = (role) => {
    setFormData(prev => ({
      ...prev,
      helpful_for: prev.helpful_for.includes(role)
        ? prev.helpful_for.filter(r => r !== role)
        : [...prev.helpful_for, role]
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddStep = () => {
    if (stepInput.trim()) {
      setFormData(prev => ({
        ...prev,
        steps: [...prev.steps, { title: stepInput.trim(), description: stepDescInput.trim() }]
      }));
      setStepInput('');
      setStepDescInput('');
    }
  };

  const handleStepDescriptionChange = (index, description) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => {
        const normalizedStep =
          typeof step === 'string'
            ? { title: step, description: '' }
            : step;
        return i === index ? { ...normalizedStep, description } : normalizedStep;
      })
    }));
  };

  const toggleStepExpanded = (index) => {
    setExpandedSteps(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleRemoveStep = (index) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  const handleMoveStep = (index, direction) => {
    const newSteps = [...formData.steps];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < newSteps.length) {
      [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
      setFormData(prev => ({ ...prev, steps: newSteps }));
    }
  };

  const handleThumbnailUpload = (files) => {
    if (files && files.length > 0) {
      setThumbnailFile(files[0]);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    }

    if (!formData.content.trim() || formData.content === '<p><br></p>') {
      newErrors.content = 'Content is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (formData.helpful_for.length === 0) {
      newErrors.helpful_for = 'Select at least one target audience';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      let thumbnailUrl = formData.thumbnail_url;

      // Save article first, then attempt thumbnail upload
      const articleData = {
        ...formData,
        thumbnail_url: thumbnailUrl
      };

      let result;
      if (isEditMode) {
        result = await HelpArticleService.updateArticle(actualArticleId, articleData);
      } else {
        result = await HelpArticleService.createArticle(articleData);
      }

      if (!result.success) {
        throw new Error(result.error || 'Failed to save article');
      }

      const savedArticleId = isEditMode ? actualArticleId : result.data?.id;

      // Upload thumbnail after article is saved (non-blocking for article creation)
      let thumbnailError = null;
      if (thumbnailFile && savedArticleId) {
        const uploadResult = await HelpArticleService.uploadThumbnail(
          thumbnailFile,
          savedArticleId
        );

        if (uploadResult.success) {
          // Update article with thumbnail URL and check the result
          const thumbnailUpdateResult = await HelpArticleService.updateArticle(savedArticleId, {
            thumbnail_url: uploadResult.url
          });

          if (!thumbnailUpdateResult?.success) {
            console.warn(
              'Article saved but failed to attach thumbnail URL:',
              thumbnailUpdateResult?.error
            );
            thumbnailError = 'Article saved but thumbnail could not be attached. You can try updating the thumbnail later.';
          }
        } else {
          console.warn('Thumbnail upload failed, article saved without thumbnail:', uploadResult.error);
          thumbnailError = 'Article saved but thumbnail upload failed. You can add a thumbnail later.';
        }
      }

      if (thumbnailError) {
        showNotification('error', thumbnailError);
      } else {
        showNotification('success', isEditMode ? 'Article updated successfully' : 'Article created successfully');
      }
      setTimeout(() => {
        navigate('/admin-moderation?tab=help-cms');
      }, 1500);
    } catch (error) {
      console.error('Error saving article:', error);
      setErrors({ submit: error.message || 'Failed to save article' });
      showNotification('error', error.message || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 pb-20 md:pb-8">
        <div className="content-container">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <button
                onClick={() => navigate('/admin-moderation?tab=help-cms')}
                className="p-2 rounded-lg border border-border hover:border-primary hover:bg-primary-50 transition-colors duration-200"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-text-primary">
                  {isEditMode ? 'Edit Article' : 'Create New Article'}
                </h1>
                <p className="text-text-secondary mt-1">
                  {isEditMode ? 'Update the article details below' : 'Fill in the details to create a new help article'}
                </p>
              </div>
            </div>
          </div>

          {/* Notification */}
          {notification && (
            <div className={`mb-6 p-4 rounded-lg ${
              notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              <div className="flex items-center">
                <Icon
                  name={notification.type === 'success' ? 'Check' : 'AlertCircle'}
                  size={20}
                  className="mr-2"
                />
                <span>{notification.message}</span>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-border p-8 max-w-4xl mx-auto">
            {/* Basic Information Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-text-primary mb-4">Basic Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Input
                  label="Title"
                  id="title"
                  name="title"
                  type="text"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  error={errors.title}
                  placeholder="e.g., How to Complete Your Profile"
                />
                <div>
                  <Input
                    label="URL Slug"
                    id="slug"
                    name="slug"
                    type="text"
                    required
                    value={formData.slug}
                    onChange={(e) => {
                      setAutoGenerateSlug(false);
                      handleInputChange(e);
                    }}
                    error={errors.slug}
                    placeholder="how-to-complete-your-profile"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Auto-generated from title (editable)
                  </p>
                </div>
              </div>

              <Textarea
                label="Short Description"
                id="description"
                name="description"
                rows={2}
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief summary of what this article covers..."
              />
            </div>

            {/* Content Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-text-primary mb-4">Article Content</h2>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rich Text Content *
              </label>
              <div className="border border-gray-300 rounded-lg overflow-hidden mb-2">
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={handleContentChange}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write your article content here..."
                  className="bg-white"
                />
              </div>
              {errors.content && (
                <p className="text-sm text-red-600">{errors.content}</p>
              )}
            </div>

            {/* Settings Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-text-primary mb-4">Settings</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="icon_name" className="block text-sm font-medium text-gray-700 mb-2">
                    Icon
                  </label>
                  <select
                    id="icon_name"
                    name="icon_name"
                    value={formData.icon_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {availableIcons.map(iconName => (
                      <option key={iconName} value={iconName}>
                        {iconName}
                      </option>
                    ))}
                  </select>
                  <div className="mt-2 flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Preview:</span>
                    <Icon name={formData.icon_name} size={20} className="text-primary" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Urgency Level
                  </label>
                  <div className="flex space-x-4">
                    {['low', 'medium', 'high'].map(level => (
                      <label key={level} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="urgency"
                          value={level}
                          checked={formData.urgency === level}
                          onChange={handleInputChange}
                          className="text-primary focus:ring-primary"
                        />
                        <span className="text-sm capitalize">{level}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <Input
                  label="Estimated Reading Time"
                  id="estimated_time"
                  name="estimated_time"
                  type="text"
                  value={formData.estimated_time}
                  onChange={handleInputChange}
                  placeholder="e.g., 5 min"
                />
              </div>
            </div>

            {/* Target Audience Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-text-primary mb-4">Target Audience</h2>
              
              <div className="flex flex-wrap gap-3 mb-2">
                {['va', 'employer', 'admin'].map(role => (
                  <label key={role} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.helpful_for.includes(role)}
                      onChange={() => handleHelpfulForToggle(role)}
                      className="rounded text-primary focus:ring-primary"
                    />
                    <span className="text-sm capitalize">{role === 'va' ? 'VA' : role}</span>
                  </label>
                ))}
              </div>
              {errors.helpful_for && (
                <p className="text-sm text-red-600">{errors.helpful_for}</p>
              )}
            </div>

            {/* Tags Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-text-primary mb-4">Tags</h2>
              
              <div className="flex space-x-2 mb-3">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  placeholder="Add a tag..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddTag}
                  iconName="Plus"
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center space-x-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                  >
                    <span>{tag}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-gray-500 hover:text-red-600"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Steps Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-text-primary mb-4">Step-by-Step Guide (Optional)</h2>
              
              <div className="space-y-2 mb-3">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={stepInput}
                    onChange={(e) => setStepInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddStep();
                      }
                    }}
                    placeholder="Add a step..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddStep}
                    iconName="Plus"
                  >
                    Add Step
                  </Button>
                </div>
                <input
                  type="text"
                  value={stepDescInput}
                  onChange={(e) => setStepDescInput(e.target.value)}
                  placeholder="Step description (optional)..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </div>
              <div className="space-y-2">
                {formData.steps.map((step, index) => {
                  const stepTitle = typeof step === 'string' ? step : step.title;
                  const stepDesc = typeof step === 'string' ? '' : (step.description || '');
                  const isExpanded = expandedSteps[index];

                  return (
                    <div
                      key={index}
                      className="bg-gray-50 rounded-lg overflow-hidden"
                    >
                      <div className="flex items-center space-x-2 p-3">
                        <button
                          type="button"
                          onClick={() => toggleStepExpanded(index)}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-transform"
                        >
                          <Icon
                            name="ChevronRight"
                            size={16}
                            className={`transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                          />
                        </button>
                        <span className="text-sm font-medium text-gray-600 w-6">
                          {index + 1}.
                        </span>
                        <span className="flex-1 text-sm">{stepTitle}</span>
                        {stepDesc && !isExpanded && (
                          <span className="text-xs text-gray-400 italic truncate max-w-[150px]">has description</span>
                        )}
                        <div className="flex space-x-1">
                          <button
                            type="button"
                            onClick={() => handleMoveStep(index, 'up')}
                            disabled={index === 0}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <Icon name="ChevronUp" size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMoveStep(index, 'down')}
                            disabled={index === formData.steps.length - 1}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <Icon name="ChevronDown" size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveStep(index)}
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <Icon name="X" size={16} />
                          </button>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-3 pb-3 pl-12">
                          <textarea
                            value={stepDesc}
                            onChange={(e) => handleStepDescriptionChange(index, e.target.value)}
                            placeholder="Add a description for this step..."
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Media Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-text-primary mb-4">Media</h2>
              
              {formData.thumbnail_url && !thumbnailFile && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Current Thumbnail:</p>
                  <img
                    src={formData.thumbnail_url}
                    alt="Current thumbnail"
                    className="w-40 h-40 object-cover rounded-lg"
                  />
                </div>
              )}
              <FileUpload
                onUpload={handleThumbnailUpload}
                accept="image/*"
                multiple={false}
                maxSize={5 * 1024 * 1024}
              />
            </div>

            {/* Publish Status Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-text-primary mb-4">Publication Status</h2>
              
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="is_published"
                  name="is_published"
                  checked={formData.is_published}
                  onChange={handleInputChange}
                  className="rounded text-primary focus:ring-primary"
                />
                <span className="text-sm font-medium text-gray-700">
                  Publish this article immediately
                </span>
              </label>
              <p className="text-sm text-gray-500 mt-2">
                {formData.is_published ? 'Article will be visible to users' : 'Article will be saved as draft'}
              </p>
            </div>

            {/* Error Message */}
            {errors.submit && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin-moderation?tab=help-cms')}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                isLoading={saving}
                iconName="Save"
                disabled={saving}
              >
                {isEditMode ? 'Update Article' : 'Create Article'}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default ArticleEditor;
